/**
 * CreditCardForm — formulário de cartão. Tokeniza via NOSSO BACKEND
 * (POST /billing/tokenize-card), que por sua vez chama
 * POST /v3/creditCard/tokenizeCreditCard no Asaas. PCI-compliant SAQ-A-EP:
 * o PAN passa pelo nosso servidor por HTTPS mas nunca é persistido —
 * só forwardado pra Asaas, que devolve o token reutilizável.
 *
 * Por que não o SDK frontend (asaas.js)? A Asaas descontinuou o SDK
 * client-side em 2025/2026 — a URL muda silenciosamente e quebra ("Erro
 * ao carregar sistema de pagamento"). A própria Asaas recomenda hoje a
 * tokenização server-side pra integrações server-driven.
 *
 * Features:
 *   • Detecção de bandeira inline (Visa/MC/Amex/Elo/Hipercard/JCB/Diners/Discover)
 *   • Mask 4-4-4-4 (4-6-5 pra Amex)
 *   • Validação Luhn + CVV-por-bandeira + expiry futuro client-side
 *   • CardPreview 3D que vira no foco do CVV
 *   • Mensagens de erro humanas em PT-BR (humanizeAsaasError)
 */
import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/form-field';
import { useToast } from '@/components/ui/toast';
import { Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import {
  detectBrand,
  formatCardNumber,
  cvvLength,
  isCardNumberValid,
  isExpiryValid,
  brandLabel,
  type CardBrand,
} from '@/lib/card-brand';
import { CardPreview } from './CardPreview';

interface AsaasErrorLike {
  code?: string;
  description?: string;
  message?: string;
}

interface CreditCardFormProps {
  /** Holder info adicional pra o backend mandar pro Asaas. */
  email: string;
  cpfCnpj: string;
  phone?: string;
  postalCode?: string;
  addressNumber?: string;
  /** Callback quando o token é gerado com sucesso. */
  onTokenGenerated: (token: string, brand: string, lastDigits: string) => void;
  onError?: (error: string) => void;
  disabled?: boolean;
  hidePreview?: boolean;
}

function humanizeAsaasError(raw: unknown, t: import('i18next').TFunction): string {
  const err = (raw ?? {}) as AsaasErrorLike;
  const code = err.code?.toLowerCase() ?? '';
  const desc = err.description ?? err.message ?? '';

  if (code.includes('invalid_number') || /n[úu]mero.*inv[áa]lido/i.test(desc))
    return t('billingUi:creditCard.errors.invalidNumber');
  if (code.includes('invalid_cvv') || /cvv.*inv[áa]lido|invalid.*cvv/i.test(desc))
    return t('billingUi:creditCard.errors.invalidCvv');
  if (code.includes('expired') || /expirado|vencido/i.test(desc))
    return t('billingUi:creditCard.errors.expired');
  if (code.includes('declined') || /recusad/i.test(desc))
    return t('billingUi:creditCard.errors.declined');
  if (/limit/i.test(desc)) return t('billingUi:creditCard.errors.limit');
  if (desc) return desc;
  return t('billingUi:creditCard.errors.generic');
}

export function CreditCardForm({
  email,
  cpfCnpj,
  phone,
  postalCode,
  addressNumber,
  onTokenGenerated,
  onError,
  disabled = false,
  hidePreview = false,
}: CreditCardFormProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [cardNumber, setCardNumber] = useState('');
  const [cardHolder, setCardHolder] = useState('');
  const [expiryMonth, setExpiryMonth] = useState('');
  const [expiryYear, setExpiryYear] = useState('');
  const [cvv, setCvv] = useState('');
  const [cvvFocused, setCvvFocused] = useState(false);
  const [touched, setTouched] = useState({
    number: false,
    exp: false,
    cvv: false,
    holder: false,
  });

  const brand: CardBrand = detectBrand(cardNumber);
  const cvvLen = cvvLength(brand);
  const numberValid = isCardNumberValid(cardNumber);
  const expValid = !!(expiryMonth && expiryYear) && isExpiryValid(expiryMonth, expiryYear);
  const cvvValid = cvv.length === cvvLen;
  const holderValid = cardHolder.trim().length >= 3;
  const formValid = numberValid && expValid && cvvValid && holderValid;

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    setCardNumber(formatted);
    const newBrand = detectBrand(formatted);
    const newCvvLen = cvvLength(newBrand);
    if (cvv.length > newCvvLen) setCvv(cvv.slice(0, newCvvLen));
  };

  const handleExpMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let v = e.target.value.replace(/\D/g, '').slice(0, 2);
    if (v.length === 1 && Number(v) > 1) v = '0' + v;
    setExpiryMonth(v);
  };

  const handleExpYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExpiryYear(e.target.value.replace(/\D/g, '').slice(0, 2));
  };

  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCvv(e.target.value.replace(/\D/g, '').slice(0, cvvLen));
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setTouched({ number: true, exp: true, cvv: true, holder: true });

      if (!numberValid) {
        toast.error(t('billingUi:creditCard.toast.invalidCardTitle'), t('billingUi:creditCard.toast.invalidCardDesc'));
        return;
      }
      if (!expValid) {
        toast.error(t('billingUi:creditCard.toast.invalidExpTitle'), t('billingUi:creditCard.toast.invalidExpDesc'));
        return;
      }
      if (!cvvValid) {
        toast.error(t('billingUi:creditCard.toast.invalidCvvTitle'), t('billingUi:creditCard.toast.invalidCvvDesc', { count: cvvLen, brand: brandLabel(brand) }));
        return;
      }
      if (!holderValid) {
        toast.error(t('billingUi:creditCard.toast.holderTitle'), t('billingUi:creditCard.toast.holderDesc'));
        return;
      }

      setIsLoading(true);
      try {
        const res = await api.request<{
          data: { creditCardToken: string; brand: string; last4: string };
        }>('/billing/tokenize-card', {
          method: 'POST',
          body: {
            holderName: cardHolder.trim(),
            number: cardNumber.replace(/\D/g, ''),
            expiryMonth,
            expiryYear,
            ccv: cvv,
            email,
            cpfCnpj,
            phone,
            postalCode,
            addressNumber,
          },
        });
        const { creditCardToken, brand: returnedBrand, last4 } = res.data;
        onTokenGenerated(creditCardToken, returnedBrand, last4);
        toast.success(t('billingUi:creditCard.toast.validatedTitle'), t('billingUi:creditCard.toast.validatedDesc'));
      } catch (err) {
        console.error('[CreditCard] Tokenization error:', err);
        const errorMsg = humanizeAsaasError(err, t);
        toast.error(t('billingUi:creditCard.toast.errorTitle'), errorMsg);
        onError?.(errorMsg);
      } finally {
        setIsLoading(false);
      }
    },
    [
      numberValid,
      expValid,
      cvvValid,
      holderValid,
      brand,
      cvvLen,
      cardHolder,
      cardNumber,
      expiryMonth,
      expiryYear,
      cvv,
      email,
      cpfCnpj,
      phone,
      postalCode,
      addressNumber,
      onTokenGenerated,
      onError,
      toast,
    ],
  );

  const showError = (key: keyof typeof touched, valid: boolean) =>
    touched[key] && !valid;

  return (
    <div className="space-y-4">
      {!hidePreview && (
        <CardPreview
          holder={cardHolder}
          number={cardNumber}
          exp={
            expiryMonth && expiryYear
              ? `${expiryMonth.padStart(2, '0')}/${expiryYear.padStart(2, '0')}`
              : ''
          }
          brand={brand}
          cvc={cvv}
          cvcLen={cvvLen}
          flipped={cvvFocused}
        />
      )}

      <div className="flex items-center justify-center gap-2 rounded-lg bg-[var(--color-accent)]/10 py-2 text-[var(--color-accent)]">
        <Lock className="h-4 w-4" />
        <span className="text-sm font-medium">
          {t('billingUi:creditCard.secureBanner')}
        </span>
      </div>

      <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
        <FormField
          label={t('billingUi:creditCard.fields.number')}
          htmlFor="creditCardNumber"
          required
          error={
            showError('number', numberValid) ? t('billingUi:creditCard.fieldErrors.invalidNumber') : undefined
          }
        >
          <div className="relative">
            <Input
              id="creditCardNumber"
              type="text"
              inputMode="numeric"
              autoComplete="cc-number"
              placeholder={brand === 'amex' ? '0000 000000 00000' : '0000 0000 0000 0000'}
              value={cardNumber}
              onChange={handleNumberChange}
              onBlur={() => setTouched((t) => ({ ...t, number: true }))}
              disabled={disabled || isLoading}
              required
              className="pr-14"
              invalid={showError('number', numberValid)}
              maxLength={brand === 'amex' ? 17 : 19}
            />
            <BrandIcon brand={brand} />
          </div>
        </FormField>

        <FormField
          label={t('billingUi:creditCard.fields.holder')}
          htmlFor="creditCardHolderName"
          required
          error={
            showError('holder', holderValid)
              ? t('billingUi:creditCard.fieldErrors.invalidHolder')
              : undefined
          }
        >
          <Input
            id="creditCardHolderName"
            type="text"
            autoComplete="cc-name"
            placeholder={t('billingUi:creditCard.fields.holderPlaceholder')}
            value={cardHolder}
            onChange={(e) => setCardHolder(e.target.value.toUpperCase())}
            onBlur={() => setTouched((t) => ({ ...t, holder: true }))}
            disabled={disabled || isLoading}
            required
            invalid={showError('holder', holderValid)}
          />
        </FormField>

        <div className="grid grid-cols-3 gap-3">
          <FormField label={t('billingUi:creditCard.fields.month')} htmlFor="creditCardExpiryMonth">
            <Input
              id="creditCardExpiryMonth"
              type="text"
              inputMode="numeric"
              autoComplete="cc-exp-month"
              placeholder="MM"
              maxLength={2}
              value={expiryMonth}
              onChange={handleExpMonthChange}
              onBlur={() => setTouched((t) => ({ ...t, exp: true }))}
              disabled={disabled || isLoading}
              required
              invalid={touched.exp && !expValid}
            />
          </FormField>
          <FormField label={t('billingUi:creditCard.fields.year')} htmlFor="creditCardExpiryYear">
            <Input
              id="creditCardExpiryYear"
              type="text"
              inputMode="numeric"
              autoComplete="cc-exp-year"
              placeholder="AA"
              maxLength={2}
              value={expiryYear}
              onChange={handleExpYearChange}
              onBlur={() => setTouched((t) => ({ ...t, exp: true }))}
              disabled={disabled || isLoading}
              required
              invalid={touched.exp && !expValid}
            />
          </FormField>
          <FormField label={t('billingUi:creditCard.fields.cvv')} htmlFor="creditCardCcv">
            <Input
              id="creditCardCcv"
              type="text"
              inputMode="numeric"
              autoComplete="cc-csc"
              placeholder={'•'.repeat(cvvLen)}
              maxLength={cvvLen}
              value={cvv}
              onChange={handleCvvChange}
              onFocus={() => setCvvFocused(true)}
              onBlur={() => {
                setCvvFocused(false);
                setTouched((t) => ({ ...t, cvv: true }));
              }}
              disabled={disabled || isLoading}
              required
              invalid={showError('cvv', cvvValid)}
            />
          </FormField>
        </div>
        {touched.exp && !expValid && (
          <p className="text-xs text-[var(--color-destructive)] -mt-2">
            {t('billingUi:creditCard.fieldErrors.invalidExp')}
          </p>
        )}
        {showError('cvv', cvvValid) && (
          <p className="text-xs text-[var(--color-destructive)] -mt-2">
            {t('billingUi:creditCard.fieldErrors.cvvLength', { count: cvvLen })}
          </p>
        )}

        <Button
          type="submit"
          variant="primary"
          className={cn('w-full gap-2 h-11', isLoading && 'cursor-not-allowed')}
          disabled={disabled || isLoading || !formValid}
          isLoading={isLoading}
          loadingText={t('billingUi:creditCard.submitLoading')}
        >
          <Lock className="h-4 w-4" />
          {t('billingUi:creditCard.submit')}
        </Button>
      </form>

      <p className="text-center text-[11px] text-[var(--color-text-muted)] leading-relaxed">
        {t('billingUi:creditCard.pciNote')}
      </p>
    </div>
  );
}

function BrandIcon({ brand }: { brand: CardBrand }) {
  return (
    <div className="absolute right-3 top-1/2 -translate-y-1/2 h-6 w-9 flex items-center justify-end pointer-events-none">
      {brand === 'unknown' ? (
        <div className="h-5 w-7 rounded-[3px] border border-[var(--color-border)] bg-[var(--color-surface-elevated)]" />
      ) : (
        <img
          src={`/brands/${brand}.png`}
          alt={brandLabel(brand)}
          draggable={false}
          className="h-5 w-auto max-w-[36px] object-contain"
        />
      )}
    </div>
  );
}
